import plotTypeService from './plotTypeService';
import { safeLookup } from '@splunk/olly-utilities/lib/sfUtilities/sfUtilities';

const TRIGGER_PLOT_PATTERN = /(data|histogram)\(\'?([^(),']*)\'?/;
const METRIC_NAME_PATTERN = /(data|histogram)\(\'([^(),']*)\'/;
const THRESHOLD_PATTERN = /^threshold\(.*\)$/;

class PlotsService {
    getPlotsInformation(
        plots = [],
        event,
        formatValue = this.formatNumberValue,
        plotColorMap = {}
    ) {
        return plots
            .sort((plotA, plotB) => {
                const first = this._isThresholdPlot(plotA);
                const second = this._isThresholdPlot(plotB);
                if (first === second) {
                    return 0;
                }
                if (first) {
                    return 1;
                }
                return -1;
            })
            .map((plot) => {
                if (this._isThresholdPlot(plot)) {
                    return {
                        key: this._getPlotKey(plot),
                        name: this._getPlotName(plot) || 'Threshold',
                        value: formatValue(plot.value),
                        rawValue: plot.value,
                        severity: this._getPlotSeverity(plot, event.metadata.sf_severity),
                    };
                }

                const roundingLength = this.getRoundingLengthForTriggerValue(plots);
                const value = plot.value;
                const resource_type = event.properties?.resource_type;

                return {
                    key: this._getPlotKey(plot),
                    name: this._getPlotName(plot, resource_type),
                    value: formatValue(value, roundingLength),
                    rawValue: value,
                    color: this._getPlotColor(plot, plotColorMap),
                    severity: this._getPlotSeverity(plot, event.metadata.sf_severity),
                };
            });
    }

    getPlotsForIncident(event, incidentInfo) {
        const allDetectorPlots = Object.values(event.properties.inputs);

        // looks like for build-in detectors, we don't get any incidentInfo,
        // so all the plots will be displayed
        const incidentRelatedPlots =
            incidentInfo && incidentInfo.selectedIdentifiers
                ? this._getRelatedPlots(allDetectorPlots, incidentInfo)
                : allDetectorPlots;

        return incidentRelatedPlots;
    }

    _getRelatedPlots(plots, incidentInfo) {
        const plotMap = this._getPlotLabelMap(plots);
        const identifiers = incidentInfo.selectedIdentifiers;

        return plots.filter((input) => {
            const label = plotMap[input.identifier];
            return identifiers.indexOf(label) !== -1;
        });
    }

    // example output: { _S1: 'A', _S2: limit_above_27_1 }
    _getPlotLabelMap(plots) {
        return plots.reduce((plotToLabelMap, input) => {
            if (input.identifiers) {
                plotToLabelMap[input.identifier] = input.identifiers[0];
            }
            return plotToLabelMap;
        }, {});
    }

    formatNumberValue(value, digitLimit = 6) {
        return parseFloat(Number(value).toFixed(digitLimit));
    }

    getTriggerPlot(plots) {
        return plots.find((plot) => plot.fragment && plot.fragment.match(TRIGGER_PLOT_PATTERN));
    }

    getRoundingLengthForTriggerValue(plots) {
        const threshold = this.getThresholdValue(plots);
        // Round to one digit longer than the threshold value by default
        // but if there is no threshold value round to 6
        return threshold ? threshold.toString().length + 1 : 6;
    }

    getTriggerValue(plots, formatValue = this.formatNumberValue) {
        const roundingLength = this.getRoundingLengthForTriggerValue(plots);
        const triggerPlot = this.getTriggerPlot(plots);
        return triggerPlot
            ? formatValue(triggerPlot.value, roundingLength)
            : formatValue(
                  plots?.filter((plot) => !plot.identifiers?.includes('fire_threshold'))?.[0]?.value
              );
    }

    getTriggerValueAsNumber(plots) {
        const roundingLength = this.getRoundingLengthForTriggerValue(plots);
        const triggerPlot = this.getTriggerPlot(plots);
        return triggerPlot ? this.formatNumberValue(triggerPlot.value, roundingLength) : '';
    }

    getThresholdValue(plots, formatValue = this.formatNumberValue) {
        const thresholdPlot = plots.find((plot) => this._isThresholdPlot(plot));
        return thresholdPlot ? formatValue(thresholdPlot.value) : '';
    }

    // NOTE: this is the temporary solution to determine the threshold direction. The correct way is to pass direction via event_annotations
    getThresholdDirection(condition) {
        if (!condition) return '>';
        const match = condition.match(/when\(\s*\w+\s*([<>]=?)\s*/);
        if (!match) return '>';
        return match[1] === '<' || match[1] === '<=' ? '<' : '>';
    }

    getSloThresholdValue(plots, formatValue = this.formatNumberValue) {
        const sloThresholdPlot = plots.find((plot) =>
            plot.identifiers?.some((i) =>
                ['error_budget_left_threshold', 'slo_threshold'].includes(i)
            )
        );
        return sloThresholdPlot ? formatValue(sloThresholdPlot.value) : '';
    }

    getTriggerPlotName(plots) {
        const triggerPlot = this.getTriggerPlot(plots);
        return triggerPlot ? this._parseMetricName(triggerPlot) : '';
    }

    _getPlotKey(plot) {
        const identifier = this._getIdentifier(plot);
        return identifier && !plotTypeService.isKnownPlotType(identifier) ? identifier : null;
    }

    _getMetricName(plot) {
        return safeLookup(plot, 'key.sf_metric');
    }

    _parseMetricName(plot) {
        const match = plot.fragment && plot.fragment.match(METRIC_NAME_PATTERN);
        return safeLookup(match, '2');
    }

    _getPlotName(plot, resource_type) {
        const identifier = this._getIdentifier(plot);
        const metricName = this._getMetricName(plot) || this._parseMetricName(plot);
        if (metricName) {
            return metricName;
        }
        if (identifier === 'd') {
            return resource_type || 'resource_request';
        }
        return plotTypeService.getDisplayFor(this._getIdentifier(plot));
    }

    _getPlotSeverity(plot, alertSeverity) {
        return this._isThresholdPlot(plot) ? alertSeverity.toLowerCase() : 'normal';
    }

    _getPlotColor(plot, plotColorMap) {
        const fragment = plot.fragment;

        const directMatch = plotColorMap[fragment];
        if (directMatch) {
            return directMatch;
        }

        const normalizedKeyMatch = Object.keys(plotColorMap).find(
            (key) => this.normalizeString(key) === this.normalizeString(fragment)
        );

        return normalizedKeyMatch ? plotColorMap[normalizedKeyMatch] : undefined;
    }

    normalizeString(value) {
        return value.replace(/[\s\\]+/g, '');
    }

    _isThresholdPlot(plot) {
        const identifier = this._getIdentifier(plot);
        const isThresholdIdentifier = identifier && plotTypeService.isThresholdType(identifier);
        const isThreshold = plot.fragment && plot.fragment.match(THRESHOLD_PATTERN);
        return isThresholdIdentifier || isThreshold;
    }

    _getIdentifier(plot) {
        return safeLookup(plot, 'identifiers.0');
    }
}

export default new PlotsService();
